import React, { useState, useEffect, useCallback } from 'react';
import styles from './AthleteGroupEditPopup.module.css';
import AthleteGroupItem from 'components/common/AthleteGroupItem';
import PropTypes from 'prop-types';
import CloseIcon from 'assets/images/icon_close.svg';
import BackIcon from 'assets/images/icon_arrow_right.svg';
import { getErrorMessage } from 'utils/api';
import {
  createClubAthleteGroup,
  getClubAthleteGroupById,
  updateClubAthleteGroupById,
  updateClubAthleteGroupMembers,
} from 'api/club/athleteGroupApi';
import { getClubMeetAthletes } from 'api/userApi';
import { toast } from 'react-toastify';
import CustomDropdown from 'components/NewDesign/CustomDropdown/CustomDropdown';
import { BOOLEAN_OPTIONS } from 'constants/options';
import useChangeDetection from 'hooks/useChangeDetection';
import CustomNewConfirm from 'components/NewDesign/CustomNewConfirm';

const AthleteGroupEditPopup = ({
  isOpen,
  onClose,
  clubID,
  group,
  mode = 'edit', // 'edit', 'add'
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [athletes, setAthletes] = useState([]);
  const [ageGroups, setAgeGroups] = useState([]);
  const [originalAthletes, setOriginalAthletes] = useState([]);
  const [selectedAthletes, setSelectedAthletes] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [ageGroupFilter, setAgeGroupFilter] = useState('all');
  const [genderFilter, setGenderFilter] = useState('all');

  const [formData, setFormData] = useState({
    groupName: '',
    baseAgeRange: '',
    sortOrder: '',
    isActive: true,
  });

  const [errors, setErrors] = useState({});
  const [originalData, setOriginalData] = useState(null);

  // Change detection
  const currentData = {
    formData,
    selectedAthletes,
  };

  const { showPrompt, confirmLeave, cancelLeave, hasChanges, setShowPrompt } =
    useChangeDetection(originalData, currentData, isOpen);

  const fetchAthletes = useCallback(async () => {
    try {
      const response = await getClubMeetAthletes(clubID);
      if (response.data) {
        setAthletes(response.data);

        const ageGroupMap = new Map();
        response.data.forEach((athlete) => {
          if (athlete.ageGroup) {
            ageGroupMap.set(athlete.ageGroup, athlete.ageGroup);
          }
        });

        const uniqueAgeGroups = [
          { value: 'all', label: 'All Age Groups' },
          ...Array.from(ageGroupMap.values())
            .map((ageGroup) => ({
              value: ageGroup,
              label: ageGroup,
            }))
            .sort((a, b) => a.value - b.value),
        ];
        setAgeGroups(uniqueAgeGroups);
      }
    } catch (err) {
      console.error('Error fetching athletes:', err);
      toast.error('Failed to load athletes');
    }
  }, [clubID]);

  const fetchGroupDetail = useCallback(async () => {
    try {
      const response = await getClubAthleteGroupById(
        clubID,
        group.athleteGroupID
      );
      if (response.data) {
        const members = response.data.members || [];
        setSelectedAthletes(members);
        setOriginalAthletes(members);
        // Update original data with the fetched members
        setOriginalData((prev) => ({
          ...prev,
          selectedAthletes: members,
        }));
      }
    } catch (err) {
      console.log('[AthleteGroups] Fetch athlete groups detail: ', err);
    }
  }, [clubID, group]);

  useEffect(() => {
    if (isOpen && clubID && group) {
      fetchGroupDetail();
    }
  }, [isOpen, clubID, group, fetchGroupDetail]);

  useEffect(() => {
    if (isOpen && clubID) {
      fetchAthletes();
    }
  }, [isOpen, clubID, fetchAthletes]);

  useEffect(() => {
    if (mode === 'edit' && group) {
      const initialFormData = {
        groupName: group.groupName || '',
        baseAgeRange: group.baseAgeRange || '',
        sortOrder: group.sortOrder || '',
        isActive: group.isActive,
      };
      setFormData(initialFormData);
      setOriginalData({
        formData: initialFormData,
        selectedAthletes: [],
      });
    } else {
      const initialFormData = {
        groupName: '',
        baseAgeRange: '',
        sortOrder: '',
        isActive: true,
      };
      setFormData(initialFormData);
      setOriginalData({
        formData: initialFormData,
        selectedAthletes: [],
      });
    }
    setSelectedAthletes([]);
    setOriginalAthletes([]);
    setErrors({});
    setSearchTerm('');
    setAgeGroupFilter('all');
    setGenderFilter('all');
  }, [mode, group]);

  const handleInputChange = (name, value) => {
    if (name === 'sortOrder') {
      const numValue = parseInt(value);
      if (
        value &&
        value.toString().trim() &&
        !isNaN(numValue) &&
        numValue < 0
      ) {
        setErrors((prev) => ({
          ...prev,
          sortOrder: 'The sort order cannot be less than 0',
        }));
        return;
      }
    }

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: '',
      }));
    }
  };

  const getFilteredAthletes = useCallback(() => {
    return athletes.filter((athlete) => {
      const matchesSearch =
        !searchTerm ||
        athlete.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        athlete.lastName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        `${athlete.firstName} ${athlete.lastName}`
          .toLowerCase()
          .includes(searchTerm.toLowerCase()) ||
        athlete.clubAthleteNumber?.toString().includes(searchTerm);

      const matchesAgeGroup =
        ageGroupFilter === 'all' ||
        athlete.ageGroup.toString() === ageGroupFilter;

      const matchesGender =
        genderFilter === 'all' || athlete.gender === genderFilter;

      return matchesSearch && matchesAgeGroup && matchesGender;
    });
  }, [athletes, searchTerm, ageGroupFilter, genderFilter]);

  const addAthlete = (athlete) => {
    if (!selectedAthletes.some((a) => a.athleteID === athlete.athleteID)) {
      setSelectedAthletes((prev) => [...prev, athlete]);
    }
  };

  const removeAthlete = (athleteId) => {
    setSelectedAthletes((prev) =>
      prev.filter((a) => a.athleteID !== athleteId)
    );
  };

  const clearAllAthletes = () => {
    setSelectedAthletes([]);
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.groupName.trim()) {
      newErrors.groupName = 'The group name is required';
    }
    if (!formData.baseAgeRange.trim()) {
      newErrors.baseAgeRange = 'The age range is required';
    }
    if (!formData.sortOrder || formData.sortOrder.toString().trim() === '') {
      newErrors.sortOrder = 'The sort order is required';
    } else if (isNaN(parseInt(formData.sortOrder))) {
      newErrors.sortOrder = 'The sort order must be a valid number';
    } else if (parseInt(formData.sortOrder) < 0) {
      newErrors.sortOrder = 'The sort order cannot be less than 0';
    }
    if (selectedAthletes.length === 0) {
      newErrors.athletes = 'Please select at least one athlete';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }
    const editedGroup = {
      groupName: formData.groupName.trim(),
      baseAgeRange: formData.baseAgeRange.trim(),
      sortOrder: parseInt(formData.sortOrder),
      isActive: formData.isActive,
      athleteIds: selectedAthletes.map((a) => a.athleteID),
    };

    if (mode === 'edit') {
      const originalAthleteIds = originalAthletes.map((a) => a.athleteID);
      const hasGroupChanges =
        group.groupName !== editedGroup.groupName ||
        group.baseAgeRange !== editedGroup.baseAgeRange ||
        group.sortOrder !== editedGroup.sortOrder ||
        group.isActive !== editedGroup.isActive;

      const hasMemberChanges =
        originalAthleteIds.length !== editedGroup.athleteIds.length ||
        !originalAthleteIds.every((id) => editedGroup.athleteIds.includes(id));

      if (!hasGroupChanges && !hasMemberChanges) {
        toast.info('No changes to save');
        onClose(true);
        return;
      }
    }

    setIsLoading(true);
    try {
      if (mode === 'edit') {
        await updateClubAthleteGroupById(
          clubID,
          group.athleteGroupID,
          editedGroup
        );
        toast.success('Athlete group updated successfully');
        await updateClubAthleteGroupMembers(clubID, group.athleteGroupID, {
          athleteIds: editedGroup.athleteIds,
        });
        toast.success('Athlete group members updated successfully');
      } else {
        await createClubAthleteGroup(clubID, editedGroup);
        toast.success('Athlete group added successfully');
      }
      setIsLoading(false);
      onClose(true);
    } catch (err) {
      console.error(err);
      toast.error(getErrorMessage(err));
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = (refresh) => {
    if (hasChanges()) {
      setShowPrompt(true);
      return;
    }
    onClose(refresh);
  };

  const handleConfirmLeave = () => {
    confirmLeave();
    onClose(false);
  };

  if (!isOpen) return null;

  const filteredAthletes = getFilteredAthletes();

  return (
    <>
      <div className="modalOverlay">
        <div className="modal bigModal">
          <div className="modalContent">
            <div className="modalTitle">
              {mode === 'add' ? 'Add Athlete Group' : 'Edit Athlete Group'}
            </div>

            <div className={styles.formContainer}>
              {/* Group Information Section */}
              <div className={styles.groupInfoSection}>
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>Group Name *</label>
                  <input
                    type="text"
                    value={formData.groupName}
                    onChange={(e) =>
                      handleInputChange('groupName', e.target.value)
                    }
                    className={`${styles.textInput} ${errors.groupName ? styles.inputError : ''}`}
                    disabled={isLoading}
                    placeholder="e.g., Sprint Squad, Distance Runners"
                  />
                  {errors.groupName && (
                    <span className={styles.errorText}>{errors.groupName}</span>
                  )}
                </div>
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>Age Range *</label>
                  <input
                    type="text"
                    value={formData.baseAgeRange}
                    onChange={(e) =>
                      handleInputChange('baseAgeRange', e.target.value)
                    }
                    className={`${styles.textInput} ${errors.baseAgeRange ? styles.inputError : ''}`}
                    disabled={isLoading}
                    placeholder="e.g., 10-12, 13-15, 16+"
                  />
                  {errors.baseAgeRange && (
                    <span className={styles.errorText}>
                      {errors.baseAgeRange}
                    </span>
                  )}
                </div>
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>Sort Order *</label>
                  <input
                    type="number"
                    value={formData.sortOrder}
                    onChange={(e) =>
                      handleInputChange('sortOrder', e.target.value)
                    }
                    className={`${styles.textInput} ${errors.sortOrder ? styles.inputError : ''}`}
                    disabled={isLoading}
                    placeholder="Enter Sort Order"
                  />
                  {errors.sortOrder && (
                    <span className={styles.errorText}>{errors.sortOrder}</span>
                  )}
                </div>
                <div className={styles.formGroup}>
                  <CustomDropdown
                    label="Visible *"
                    name="isActive"
                    value={formData.isActive}
                    onChange={handleInputChange}
                    options={BOOLEAN_OPTIONS}
                    error={errors.isActive}
                    disabled={isLoading}
                    placeholder=""
                    buttonClassName={styles.customDropdownButton}
                  />
                  {errors.isActive && (
                    <span className={styles.errorText}>{errors.isActive}</span>
                  )}
                </div>
              </div>

              {/* Athlete Selection Section */}
              <div className={styles.athleteSelectionSection}>
                {/* Available Athletes Column */}
                <div className={styles.selectionColumn}>
                  <div className={styles.columnHeader}>Available Athletes</div>

                  <div className={styles.filtersContainer}>
                    <div className={styles.searchWrapper}>
                      <input
                        type="text"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className={styles.searchInput}
                        placeholder="Search by name or number..."
                      />
                      {searchTerm && (
                        <button
                          className={styles.clearSearch}
                          onClick={() => setSearchTerm('')}
                        >
                          ×
                        </button>
                      )}
                    </div>
                    <select
                      value={ageGroupFilter}
                      onChange={(e) => setAgeGroupFilter(e.target.value)}
                      className={styles.filterSelect}
                    >
                      {ageGroups?.map((option, index) => (
                        <option key={index} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                    <select
                      value={genderFilter}
                      onChange={(e) => setGenderFilter(e.target.value)}
                      className={styles.filterSelect}
                    >
                      <option value="all">All Genders</option>
                      <option value="M">Male</option>
                      <option value="F">Female</option>
                    </select>
                  </div>

                  <div className={styles.athletesList}>
                    {filteredAthletes.length === 0 ? (
                      <div className={styles.emptyState}>
                        No athletes found matching filters
                      </div>
                    ) : (
                      filteredAthletes.map((athlete) => {
                        const isSelected = selectedAthletes.some(
                          (a) => a.athleteID === athlete.athleteID
                        );
                        return (
                          <AthleteGroupItem
                            key={athlete.athleteID}
                            athlete={athlete}
                            isSelected={isSelected}
                            isDisabled={isSelected}
                            onAdd={addAthlete}
                            variant="available"
                          />
                        );
                      })
                    )}
                  </div>
                </div>

                {/* Selected Athletes Column */}
                <div className={styles.selectionColumn}>
                  <div className={styles.columnHeader}>Selected Athletes</div>

                  <div className={styles.selectionActions}>
                    <span className={styles.selectionInfo}>
                      {selectedAthletes.length} athlete
                      {selectedAthletes.length === 1 ? '' : 's'} selected
                    </span>
                    {selectedAthletes.length > 0 && (
                      <button
                        className={styles.clearAllBtn}
                        onClick={clearAllAthletes}
                      >
                        Clear All
                      </button>
                    )}
                  </div>

                  <div className={styles.selectedAthletesList}>
                    {selectedAthletes.length === 0 ? (
                      <div className={styles.emptyState}>
                        No athletes selected yet
                      </div>
                    ) : (
                      selectedAthletes.map((athlete) => (
                        <AthleteGroupItem
                          key={athlete.athleteID}
                          athlete={athlete}
                          onRemove={removeAthlete}
                          showRemoveButton={true}
                          variant="selected"
                        />
                      ))
                    )}
                  </div>
                </div>
              </div>

              {errors.athletes && (
                <div className={styles.errorText}>{errors.athletes}</div>
              )}
            </div>

            <div className={styles.actionButtons}>
              <button
                className={styles.btnCancel}
                onClick={() => handleClose(false)}
              >
                Cancel
              </button>
              <button
                className={styles.btnSave}
                onClick={handleSave}
                disabled={isLoading}
              >
                {isLoading ? 'Saving...' : 'Save Group'}
              </button>
            </div>

            <div className="goBack" onClick={() => handleClose(false)}>
              <div className="goBackImage">
                <img src={BackIcon} alt="back" />
              </div>
              <div className="goBackText">Back</div>
            </div>
            <div className="closeBtn" onClick={() => handleClose(false)}>
              <img src={CloseIcon} alt="close" />
            </div>
          </div>
        </div>
      </div>

      {/* Change Detection Confirmation Dialog */}
      <CustomNewConfirm
        isOpen={showPrompt}
        onClose={cancelLeave}
        title="Discard Changes?"
        description="You have unsaved changes. Are you sure you want to leave without saving?"
        onConfirm={handleConfirmLeave}
        confirmButtonText="Discard Changes"
        confirmButtonVariant="danger"
        onCancel={cancelLeave}
        cancelButtonText="Continue Editing"
      />
    </>
  );
};

AthleteGroupEditPopup.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  clubID: PropTypes.string.isRequired,
  mode: PropTypes.string,
  group: PropTypes.object,
};

export default AthleteGroupEditPopup;
